import type { ArtefactReviewItem } from "./types";

export const PROCESS_PLAN_CATALOGUE_KEY = "octopus_process_plan";
export const ADVICE_PACK_CATALOGUE_KEY = "octopus_generate_advice_pack";
export const DRAFT_NARRATIVE_CATALOGUE_KEY = "octopus_draft_narrative";
export const DRAFT_SUITABILITY_CATALOGUE_KEY = "octopus_draft_suitability_letter";
export const STARTER_SESSION_CATALOGUE_KEY = "octopus_generate_starter_session_deck";

/** True when an artefact row should use the cheat sheet preview path. */
export function isCheatSheetReviewArtefact(
  artefact: ArtefactReviewItem,
  catalogueKey?: string | null,
): boolean {
  if (artefact.artifact_kind === "plan_cheat_sheet") return true;
  if (artefact.artifact_kind === "advice_session_deck") return false;
  if (artefact.artifact_kind === "starter_session_deck") return false;
  if (artefact.artifact_kind === "advice_session_advisor_notes") return false;
  if (artefact.artifact_kind === "client_narrative") return false;
  if (artefact.artifact_kind === "suitability_letter") return false;
  if (artefact.artifact_kind === "advisor_notes") return false;
  if (artefact.artifact_kind === "loa") return false;

  if (catalogueKey === PROCESS_PLAN_CATALOGUE_KEY) return true;
  if (catalogueKey === ADVICE_PACK_CATALOGUE_KEY) return false;
  if (catalogueKey === DRAFT_NARRATIVE_CATALOGUE_KEY) return false;
  if (catalogueKey === DRAFT_SUITABILITY_CATALOGUE_KEY) return false;
  if (catalogueKey === STARTER_SESSION_CATALOGUE_KEY) return false;

  if (
    artefact.cheat_sheet_mode != null ||
    artefact.cheat_sheet_variant != null ||
    artefact.cheat_sheet_uses_proxy != null
  ) {
    return true;
  }

  const hasReportPreview =
    Boolean(artefact.report_config_id?.trim()) ||
    artefact.preview_path?.includes("report-builder") === true ||
    artefact.viewer_url?.includes("report-builder") === true;

  if (hasReportPreview && !artefact.template_key) return true;

  return false;
}

/** True when an artefact row is an advice session deck (advice pack review). */
export function isAdviceDeckReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return artefact.artifact_kind === "advice_session_deck";
}

/** True when an artefact row is a starter session deck PDF. */
export function isStarterSessionDeckReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return artefact.artifact_kind === "starter_session_deck";
}

/** Deck PDFs rendered via report-builder (advice pack or starter session). */
export function isReportDeckReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return isAdviceDeckReviewArtefact(artefact) || isStarterSessionDeckReviewArtefact(artefact);
}

/** True when an artefact row is the advisor-notes Word doc from an advice pack render. */
export function isAdvisorNotesReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return artefact.artifact_kind === "advice_session_advisor_notes";
}

/** True when an artefact row is a draft paraplanner narrative form. */
export function isClientNarrativeReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return artefact.artifact_kind === "client_narrative";
}

/** True when an artefact row is a suitability letter body. */
export function isSuitabilityLetterReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return artefact.artifact_kind === "suitability_letter";
}

/** True when an artefact row is paired advisor notes from draft suitability. */
export function isSuitabilityAdvisorNotesReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return artefact.artifact_kind === "advisor_notes";
}

/** Inline text preview via ``/preview-text`` (DOCX blob or TipTap body). */
export function isDocumentTextReviewArtefact(artefact: ArtefactReviewItem): boolean {
  return (
    isAdvisorNotesReviewArtefact(artefact) ||
    isClientNarrativeReviewArtefact(artefact) ||
    isSuitabilityLetterReviewArtefact(artefact) ||
    isSuitabilityAdvisorNotesReviewArtefact(artefact)
  );
}
